import { useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { GripVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TASK_FOOTER_H, TASK_HEADER_H, TASK_MAX_TEXT, TASK_MAX_TITLE, TASK_ROW_H, TASK_ROW_H_COMPACT, taskProgress, type SceneStore, type TaskItem, type TaskList } from '@os/doc';
import { OBJECT_COLORS } from '../../ui/palette';
import { contrastRatio } from '../../ui/contrast';
import { TaskEditor, type TaskEditMove } from '../tasks/TaskEditor';
import { onTaskEditRequest, requestTaskEdit, type TaskEditTarget } from '../tasks/task-edit';
import { useListLocked } from '../tasks/useListLocked';
import { dropIndex } from '../tasks/reorder';
import { worldPerScreenPx } from './zoom';
import { TaskAssign } from './TaskAssign';
import { CanvasPeopleContext } from '../editor/CanvasAuthors';
import { ExportModeContext } from '../editor/embeds';
import { relativeTime } from '../dashboard/cards';

const INK = '#141414';
export const TASK_DEFAULT_ACCENT: string = OBJECT_COLORS.find((c) => c.id === 'blue')!.hex;
/** World px of left padding per indent level. */
export const TASK_INDENT_PX = 20;
const HEX = /^#[0-9a-f]{6}$/i;

/** The list's colour (its header band), from `style.accent`. */
export const accentOf = (o: TaskList): string => (typeof o.style.accent === 'string' && HEX.test(o.style.accent) ? o.style.accent : TASK_DEFAULT_ACCENT);
/** Dark text on a light band, white on a dark one. */
const inkOn = (bg: string) => (contrastRatio(INK, bg) >= 4.5 ? INK : '#ffffff');

/**
 * A task list (P16-5): a coloured header with the title and the progress, one fixed-height row per task, a footer to
 * add one. The checkbox is real (transparent, over a drawn box the export can see: Ruling 6) and checks without
 * selecting the list (Ruling 7); everything else belongs to the canvas, which asks for edits through `requestTaskEdit`.
 */
export function TaskListObject({ o, store }: { o: TaskList; store: SceneStore }) {
  const { t, i18n } = useTranslation();
  const people = useContext(CanvasPeopleContext);
  /** "Checked by Ada · 2 hours ago" (Ruling 8): on server boards, for a task checked with a trace. */
  const traceOf = (item: TaskItem): string | undefined => {
    if (!item.done || !item.doneBy || !people) return undefined;
    const who = people.person(item.doneBy);
    if (!who) return undefined;
    const name = who.you ? t('tasks.byYou') : who.name;
    return item.doneAt !== undefined ? t('tasks.doneBy', { name, when: relativeTime(new Date(item.doneAt).toISOString(), i18n.language) }) : t('tasks.doneByNoTime', { name });
  };
  const readOnly = useSyncExternalStore(
    (cb) => store.subscribeReadOnly(cb),
    () => store.readOnly,
  );
  const locked = useListLocked(store, o.id);
  // An export draws the content only: no grip, no add button (Ruling 6: what it shows is what the board says).
  const exporting = useContext(ExportModeContext);
  const editable = !readOnly && !locked && !exporting;
  // `seq` remounts the editor when the same target opens again (after an indent or a move it keeps editing).
  const [editing, setEditing] = useState<{ target: TaskEditTarget; seq: number } | null>(null);
  const edit = (target: TaskEditTarget | null) => setEditing((cur) => (target ? { target, seq: (cur?.seq ?? 0) + 1 } : null));
  useEffect(() => onTaskEditRequest(store, o.id, (target) => !store.readOnly && edit(target)), [store, o.id]);
  const editingItem = editing && editing.target !== 'title' ? editing.target.itemId : null;
  const commitTitle = (text: string, next: TaskEditMove) => {
    const c = store.commands;
    if (text !== o.title) c.taskSetTitle(o.id, text);
    if (next !== 'newBelow' && next !== 'indent' && next !== 'down') return edit(null);
    const first = (store.getObject(o.id) as TaskList | undefined)?.items[0]?.id ?? c.taskAdd(o.id, 0);
    edit(first ? { itemId: first } : null);
  };
  /** What a key in a task's editor means for the list (P16-5 Task 5). Every write is its own undo step. */
  const commitTask = (itemId: string, text: string, next: TaskEditMove) => {
    const c = store.commands;
    const items = (store.getObject(o.id) as TaskList | undefined)?.items ?? [];
    const i = items.findIndex((t) => t.id === itemId);
    const item = items[i];
    if (!item) return edit(null);
    if (text !== item.text) c.taskSetText(o.id, itemId, text);
    const again = () => edit({ itemId });
    switch (next) {
      case 'none':
        return edit(null);
      case 'newBelow': {
        if (text === '') {
          if (items.length > 1) c.taskDelete(o.id, itemId);
          return edit(null);
        }
        const made = c.taskAdd(o.id, i + 1);
        if (made && item.indent) c.taskIndent(o.id, made, item.indent);
        return edit(made ? { itemId: made } : null);
      }
      case 'deleteEmpty': {
        c.taskDelete(o.id, itemId);
        const near = items[i - 1] ?? items[i + 1];
        return edit(near ? { itemId: near.id } : 'title');
      }
      case 'up':
        return edit(i > 0 ? { itemId: items[i - 1]!.id } : 'title');
      case 'down':
        return edit(items[i + 1] ? { itemId: items[i + 1]!.id } : null);
      case 'indent':
      case 'outdent':
        c.taskIndent(o.id, itemId, item.indent + (next === 'indent' ? 1 : -1));
        return again();
      case 'moveUp':
      case 'moveDown':
        c.taskMove(o.id, itemId, i + (next === 'moveUp' ? -1 : 1));
        return again();
      case 'toggle':
        c.taskSetDone(o.id, itemId, !item.done);
        return again();
    }
  };
  const accent = accentOf(o);
  const ink = inkOn(accent);
  const { done, total } = taskProgress(o.items);
  const rowH = o.compact ? TASK_ROW_H_COMPACT : TASK_ROW_H;
  // Reordering by a row's grip (Task 6): the row follows the pointer, one `taskMove` on release, Escape cancels.
  const [drag, setDrag] = useState<{ itemId: string; dy: number } | null>(null);
  const start = useRef<{ itemId: string; from: number; y: number; scale: number } | null>(null);
  useEffect(() => {
    if (!drag) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      start.current = null;
      setDrag(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drag]);
  const grip = (itemId: string, from: number) => ({
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      start.current = { itemId, from, y: e.clientY, scale: worldPerScreenPx(e.currentTarget) };
      setDrag({ itemId, dy: 0 });
    },
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
      const s = start.current;
      if (s?.itemId === itemId) setDrag({ itemId, dy: (e.clientY - s.y) * s.scale });
    },
    onPointerUp: (e: React.PointerEvent<HTMLElement>) => {
      const s = start.current;
      start.current = null;
      setDrag(null);
      if (s?.itemId !== itemId) return;
      const to = dropIndex(s.from, (e.clientY - s.y) * s.scale, rowH, o.items.length);
      if (to !== s.from) store.commands.taskMove(o.id, itemId, to);
    },
    onPointerCancel: () => {
      start.current = null;
      setDrag(null);
    },
  });
  const add = () => {
    const made = store.commands.taskAdd(o.id, o.items.length);
    if (made) requestTaskEdit(store, o.id, { itemId: made });
  };
  return (
    <div data-tasklist="" className="flex h-full w-full flex-col overflow-hidden rounded-[10px] border border-[#e4e4e7] bg-white font-sans text-[#141414] shadow-[0_1px_4px_rgb(0_0_0/0.08)]">
      <div className="flex shrink-0 flex-col justify-center gap-1.5 px-3" style={{ height: TASK_HEADER_H, background: accent, color: ink }}>
        <div className="flex items-baseline gap-2">
          <span data-task="title" className={`min-w-0 flex-1 text-[15px] font-semibold leading-6 ${editing?.target === 'title' && editable ? 'flex' : 'truncate'} ${o.title || editing?.target === 'title' ? '' : 'opacity-60'}`}>
            {editing?.target === 'title' && editable ? (
              <TaskEditor key={editing.seq} value={o.title} maxLength={TASK_MAX_TITLE} label={t('tasks.editTitle')} onCommit={commitTitle} onCancel={() => edit(null)} />
            ) : (
              o.title || t('tasks.placeholder')
            )}
          </span>
          <span className="shrink-0 text-xs font-medium tabular-nums">
            {done}/{total}
          </span>
        </div>
        <div
          role="progressbar"
          aria-label={t('tasks.progress', { done, total })}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={done}
          className="h-1.5 w-full overflow-hidden rounded-full"
          style={{ background: ink === INK ? 'rgb(0 0 0 / 0.12)' : 'rgb(255 255 255 / 0.25)' }}
        >
          <div className="h-full rounded-full" style={{ width: total ? `${(done / total) * 100}%` : 0, background: ink }} />
        </div>
      </div>
      <ul role="list" className="m-0 list-none p-0">
        {o.items.map((item, i) => {
          const trace = traceOf(item);
          return (
          <li
            key={item.id}
            data-task={item.id}
            {...(item.done ? { 'data-done': '' } : {})}
            // Touch keeps a lane for the grip left of the checkbox (their hit areas never overlap).
            className={`group relative flex items-center gap-2 pr-3 pl-[calc(12px+var(--task-indent))] pointer-coarse:pl-[calc(28px+var(--task-indent))] ${o.compact ? 'text-[13px]' : 'text-sm'} ${drag?.itemId === item.id ? 'z-10 bg-white shadow-[0_2px_8px_rgb(0_0_0/0.15)]' : ''}`}
            style={{ height: rowH, '--task-indent': `${item.indent * TASK_INDENT_PX}px`, transform: drag?.itemId === item.id ? `translateY(${drag.dy}px)` : '' } as React.CSSProperties}
            title={trace}
          >
            {editable && (
              <span
                data-task-grip=""
                data-canvas-ignore=""
                aria-hidden="true"
                {...grip(item.id, i)}
                className={`absolute top-0 z-10 flex h-full w-3 cursor-grab touch-none items-center pointer-coarse:w-3.5 justify-center text-[#a1a1aa] active:cursor-grabbing ${editingItem === item.id || drag?.itemId === item.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 pointer-coarse:opacity-100'}`}
                style={{ left: item.indent * TASK_INDENT_PX }}
              >
                <GripVertical size={12} strokeWidth={2} />
              </span>
            )}
            <span
              data-check=""
              className={`relative inline-flex shrink-0 items-center justify-center rounded-[5px] border-[1.5px] ${o.compact ? 'size-[14px]' : 'size-[18px]'}`}
              style={item.done ? { background: INK, borderColor: INK } : { background: '#ffffff', borderColor: '#a1a1aa' }}
            >
              {item.done && (
                <svg viewBox="0 0 16 16" aria-hidden="true" className="size-full">
                  <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              <input
                type="checkbox"
                data-canvas-ignore=""
                tabIndex={-1}
                checked={item.done}
                disabled={!editable}
                aria-label={item.text || t('tasks.untitled')}
                aria-description={trace}
                onChange={(e) => store.commands.taskSetDone(o.id, item.id, e.currentTarget.checked)}
                // A click checks without taking the focus: the canvas keeps its shortcuts (undo right after, P16-5).
                onMouseDown={(e) => e.preventDefault()}
                // 44 px wide to the finger, never taller than its own row (rows are shared geometry, Ruling 4).
                className={`absolute -inset-1.5 m-0 cursor-pointer opacity-0 disabled:cursor-default ${o.compact ? 'pointer-coarse:-inset-x-[16px] pointer-coarse:-inset-y-[7px]' : 'pointer-coarse:-inset-x-[15px] pointer-coarse:-inset-y-[10px]'}`}
              />
            </span>
            {editingItem === item.id && editable ? (
              <TaskEditor key={editing!.seq} value={item.text} maxLength={TASK_MAX_TEXT} label={t('tasks.editTask')} onCommit={(text, next) => commitTask(item.id, text, next)} onCancel={() => edit(null)} />
            ) : (
              <span className={`min-w-0 flex-1 truncate ${item.done ? 'text-[#71717a] line-through' : ''}`}>{item.text}</span>
            )}
            <TaskAssign store={store} listId={o.id} item={item} disabled={!editable} />
          </li>
          );
        })}
      </ul>
      <div className="mt-auto flex shrink-0 items-center px-2" style={{ height: TASK_FOOTER_H }}>
        {editable && (
          <button type="button" data-canvas-ignore="" onClick={add} className="flex h-9 items-center gap-1 rounded-md px-2 text-[13px] pointer-coarse:h-11 text-[#52525b] hover:bg-[#f4f4f5] hover:text-[#141414]">
            <span aria-hidden="true">+</span>
            {t('tasks.add')}
          </button>
        )}
      </div>
    </div>
  );
}
